import { Coins } from "lucide-react";
import Flag from "react-world-flags";

import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import useTranslations from "@/shared/lang/useTranslations";

import { getColorForCurrency } from "../constants/walletColors.constants";
import { isCryptoCurrency } from "../constants/walletMethods.constants";
import {
  CURRENCY_TO_COUNTRY,
  CURRENCY_TO_DISPERSION_CODE,
  CURRENCY_TO_FLAG_CODE,
} from "../constants/wallets.constants";
import type { RuntimeWallet } from "../types/payments-wallets.types";
import { currencyFractionDigits, resolveLocale } from "../utils/formatWalletAmount";

export interface WalletTargetSummaryProps {
  wallet: RuntimeWallet;
}

/**
 * Identity of the wallet a status action is about to hit: country · currency, the owning organization and the
 * available balance.
 *
 * Both status dialogs render it. On the wallets home the action is fired from one card out of a grid of
 * look-alikes, so the confirmation has to say _which_ wallet it means — and it is shared rather than inlined
 * twice so freeze and unfreeze cannot drift apart in what they disclose.
 */
export function WalletTargetSummary({ wallet }: WalletTargetSummaryProps) {
  const { t, activeLanguage } = useTranslations("wallets");

  const isCrypto = isCryptoCurrency(wallet.currency);
  const accentColor = getColorForCurrency(wallet.currency);
  const countryName = CURRENCY_TO_COUNTRY[wallet.currency] ?? "";
  const flagCode = !isCrypto
    ? (CURRENCY_TO_FLAG_CODE[wallet.currency] ?? CURRENCY_TO_DISPERSION_CODE[wallet.currency] ?? null)
    : null;
  const decimals = currencyFractionDigits(wallet.currency);

  return (
    <div className="flex items-center gap-3 rounded-md border border-border bg-muted/40 px-3 py-2.5 text-left">
      <div
        className="grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded-lg border"
        style={{
          backgroundColor: `color-mix(in oklch, ${accentColor} 10%, transparent)`,
          borderColor: `color-mix(in oklch, ${accentColor} 24%, transparent)`,
        }}
        aria-hidden="true"
      >
        {isCrypto || !flagCode ? (
          <Coins size={16} style={{ color: accentColor }} />
        ) : (
          <Flag code={flagCode} className="h-4 w-6 rounded-[2px] object-cover" aria-hidden />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">
          {countryName ? `${countryName} · ${wallet.currency}` : wallet.currency}
        </p>
        <p className="truncate text-xs text-muted-foreground">{wallet.organization.name}</p>
      </div>
      <div className="shrink-0 text-right">
        <ColocaAmountDisplay
          value={wallet.balance_available}
          currency={wallet.currency}
          minimumFractionDigits={decimals.min}
          maximumFractionDigits={decimals.max}
          locale={resolveLocale(activeLanguage)}
          size="sm"
          className="block text-foreground"
        />
        <p className="mt-0.5 font-mono text-[9.5px] uppercase tracking-wider text-muted-foreground">
          {t("card_available_label")}
        </p>
      </div>
    </div>
  );
}
